import { Component, ElementRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { OverlayModule } from '@angular/cdk/overlay';
import {
  ButtonComponent,
  DrawerComponent,
  IconComponent,
  InputSelectComponent,
  UserMenuDropdownComponent,
  UserProfileTriggerComponent
} from '@jdi/jdi-ui-kit';
import { firstValueFrom } from 'rxjs';

import { NavItemWithAuth, PRIMARY_NAV_ITEMS } from '../../constants/navigation.constants';
import { AuthStateService } from '../../auth/services/auth-state.service';
import { AuthorizationService } from '../../auth/services/authorization.service';
import { AuthSessionService } from '../../auth/services/auth-session.service';
import { UserContextService } from '../../auth/services/user-context.service';
import { LocaleService } from '../../services/locale.service';
import { ShipmentsDataService } from '../../../features/shipments/services/shipments-data.service';
import { environment } from '../../../../environments/environment';

const THEME_STORAGE_KEY = 'app_theme';
const LOGO_BY_LANGUAGE: Record<string, string> = {
  'en-ca': '/assets/logos/MyMidland_logo.svg',
  'fr-ca': '/assets/logos/MyMidland_logo_fr.svg'
};

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    OverlayModule,
    ButtonComponent,
    DrawerComponent,
    IconComponent,
    InputSelectComponent,
    UserMenuDropdownComponent,
    UserProfileTriggerComponent
  ],
  templateUrl: './app-header.component.html',
  host: {
    class: 'z-20'
  }
})
export class AppHeaderComponent {
  @ViewChild('userMenuContainer', { read: ElementRef })
  private readonly userMenuContainer?: ElementRef<HTMLElement>;

  protected readonly environmentName = environment.environmentName;
  protected readonly enableEnvBanner = environment.enableEnvBanner;
  protected readonly navItems = PRIMARY_NAV_ITEMS;
  protected readonly userMenuDropdownId = 'app-header-user-menu';
  protected readonly userMenuPanelAriaLabel = $localize`:@@headerUserMenuAriaLabel:User menu`;
  protected readonly primaryNavigationAriaLabel = $localize`:@@headerPrimaryNavigationAriaLabel:Primary navigation`;
  protected readonly accountDetailsAriaLabel = $localize`:@@headerAccountDetailsAriaLabel:Account details`;
  protected readonly languageLabel = $localize`:@@headerLanguageLabel:Language`;
  protected readonly mainNavigationAriaLabel = $localize`:@@headerMainNavigationAriaLabel:Main navigation`;
  protected readonly languageOptions = [
    { label: $localize`:@@headerEnglishLanguageOptionLabel:English`, value: 'en-ca' },
    { label: 'Français', value: 'fr-ca' }
  ];
  protected readonly accountSectionLabel = $localize`:@@headerAccountSectionLabel:Account`;
  protected readonly preferencesSectionLabel = $localize`:@@headerPreferencesSectionLabel:Preferences`;
  protected readonly nameLabel = $localize`:@@headerNameLabel:Name`;
  protected readonly roleLabel = $localize`:@@headerRoleLabel:Role`;
  protected readonly phoneLabel = $localize`:@@headerPhoneLabel:Phone`;
  protected readonly darkThemeOnLabel = $localize`:@@headerDarkThemeOnLabel:Dark Theme: On`;
  protected readonly darkThemeOffLabel = $localize`:@@headerDarkThemeOffLabel:Dark Theme: Off`;
  protected readonly goToHomeAriaLabel = $localize`:@@headerGoToHomeAriaLabel:Go to MyMidland home`;
  protected readonly openMainNavAriaLabel = $localize`:@@headerOpenMainNavAriaLabel:Open main navigation`;
  protected readonly closeMainNavAriaLabel = $localize`:@@headerCloseMainNavAriaLabel:Close main navigation`;
  protected readonly unknownUserLabel = $localize`:@@headerUnknownUserLabel:Unknown User`;
  protected readonly unassignedRoleLabel = $localize`:@@headerUnassignedRoleLabel:Unassigned`;
  protected readonly phoneUnavailableLabel = $localize`:@@headerPhoneUnavailableLabel:Not available`;

  protected getOpenNavItemAriaLabel(label: string): string {
    return $localize`:@@headerOpenNavItemAriaLabel:Open ${label}:navItemLabel:`;
  }

  protected selectedLanguage = 'en-ca';
  protected themeEnabled = false;
  protected userMenuOpen = false;
  protected mobileNavDrawerOpen = false;

  protected get logoPath(): string {
    return LOGO_BY_LANGUAGE[this.selectedLanguage] ?? LOGO_BY_LANGUAGE['en-ca'];
  }

  protected get navigationLocale(): string {
    return this.localeService.getLocaleFromPath(this.router.url) ?? this.selectedLanguage;
  }

  constructor(
    private readonly router: Router,
    private readonly authStateService: AuthStateService,
    private readonly authorizationService: AuthorizationService,
    private readonly authSessionService: AuthSessionService,
    private readonly userContextService: UserContextService,
    protected readonly localeService: LocaleService,
    private readonly shipmentsDataService: ShipmentsDataService
  ) {
    this.selectedLanguage = this.localeService.getCurrentLocale();
    this.themeEnabled = this.isDarkThemeEnabled();
    this.applyTheme(this.themeEnabled);
  }

  protected get visibleNavItems(): NavItemWithAuth[] {
    if (environment.name === 'local') {
      return this.navItems;
    }

    return this.navItems.filter((item) => this.authorizationService.hasAccess(item.auth));
  }

  protected get userName(): string {
    return this.authStateService.user()?.displayName ?? this.unknownUserLabel;
  }

  protected get avatarInitials(): string {
    const nameParts = this.userName.split(' ').filter((segment) => segment.length > 0);

    if (!nameParts.length) {
      return 'NA';
    }

    if (nameParts.length === 1) {
      return nameParts[0].slice(0, 2).toUpperCase();
    }

    return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
  }

  protected get avatarInfoItems() {
    const user = this.authStateService.user();
    const role = user?.role?.replace(/redirect/gi, '').trim() || this.unassignedRoleLabel;

    return [
      {
        icon: 'user',
        label: this.nameLabel,
        value: user?.displayName ?? this.unknownUserLabel
      },
      {
        icon: 'user-check',
        label: this.roleLabel,
        value: role
      },
      {
        icon: 'phone',
        label: this.phoneLabel,
        value: user?.phoneNumber ?? this.phoneUnavailableLabel
      }
    ];
  }

  protected openMobileNavDrawer(): void {
    this.userMenuOpen = false;
    this.mobileNavDrawerOpen = true;
  }

  protected closeMobileNavDrawer(): void {
    this.mobileNavDrawerOpen = false;
  }

  protected toggleUserMenu(): void {
    this.mobileNavDrawerOpen = false;
    this.userMenuOpen = !this.userMenuOpen;
  }

  protected closeUserMenu(): void {
    this.userMenuOpen = false;
  }

  protected navigateFromMobileDrawer(item: NavItemWithAuth): void {
    this.closeMobileNavDrawer();
    this.navigate(item);
  }

  protected navigate(item: NavItemWithAuth): void {
    if (item.kind === 'external') {
      const target = item.target ?? '_blank';
      const openedWindow = window.open(item.href, target, 'noopener,noreferrer');

      if (openedWindow) {
        openedWindow.opener = null;
      }

      return;
    }

    const localizedPath = this.localeService.withLocalePath(item.href, this.navigationLocale);
    void this.navigateInternal(localizedPath);
  }

  protected isNavItemDisabled(item: NavItemWithAuth): boolean {
    if(item.href === '/statement-billing') {
      return !this.shipmentsDataService.statementBillingEnabled();
    }
    return false;
  }

  protected onLogoClick(event: Event): void {
    event.preventDefault();

    const localizedHomePath = this.localeService.withLocalePath('/shipments', this.navigationLocale);
    const currentPath = this.normalizePath(this.router.url);
    const homePath = this.normalizePath(localizedHomePath);

    if (currentPath === homePath) {
      this.shipmentsDataService.requestHomeRefresh();
      return;
    }

    void this.navigateInternal(localizedHomePath);
  }

  private async navigateInternal(localizedPath: string): Promise<void> {
    const targetLocale = this.localeService.getLocaleFromPath(localizedPath);
    const currentLocale = this.localeService.getLocaleFromPath(this.router.url);
    const routerPath = this.localeService.stripSupportedLocalePrefix(localizedPath);

    if (targetLocale && currentLocale && targetLocale !== currentLocale) {
      this.navigateDocument(localizedPath);
      return;
    }

    try {
      await this.router.navigateByUrl(routerPath);
    } catch {
      // Keep router failures inside the application instead of exposing an Nginx error page.
    }
  }

  private navigateDocument(path: string): void {
    window.location.assign(path);
  }

  private normalizePath(path: string): string {
    const withoutQuery = path.split('?')[0].split('#')[0] || '/';
    const routerPath = this.localeService.stripSupportedLocalePrefix(withoutQuery);
    return this.localeService.stripSupportedLocalePrefix(
      this.localeService.withLocalePath(routerPath, this.navigationLocale)
    );
  }

  protected isActive(item: NavItemWithAuth): boolean {
    if (item.kind !== 'internal') {
      return false;
    }

    const localizedPath = this.localeService.withLocalePath(item.href, this.navigationLocale);
    const localeStrippedPath = this.localeService.stripSupportedLocalePrefix(localizedPath);
    const currentPath = this.normalizePath(this.router.url);

    return currentPath === localeStrippedPath;
  }

  protected updateLanguage(value: string): void {
    void this.applyLanguageSelection(value);
  }

  private async applyLanguageSelection(value: string): Promise<void> {
    const normalized = this.localeService.normalizePreferredLocale(value);

    if (!normalized) {
      return;
    }

    if (normalized === this.selectedLanguage) {
      return;
    }

    const user = this.authStateService.user();

    if (user?.userId) {
      try {
        const wasUpdated = await firstValueFrom(this.userContextService.updateLanguagePreference(user.userId, normalized));

        if (!wasUpdated) {
          return;
        }
      } catch {
        return;
      }

      this.authStateService.updatePreferredLanguage(normalized);
    }

    this.selectedLanguage = this.localeService.setPreferredLocale(normalized);
    window.location.href = this.localeService.buildLocalizedAbsoluteUrl(this.selectedLanguage, this.router.url);
  }

  protected updateTheme(value: boolean): void {
    this.themeEnabled = value;
    this.storeThemePreference(value);
    this.applyTheme(value);
  }

  protected signOut(): void {
    this.authSessionService.signOut();
  }

  private isDarkThemeEnabled(): boolean {
    const storedTheme = this.readStoredThemePreference();

    if (storedTheme) {
      return storedTheme === 'dark';
    }

    if (typeof document === 'undefined') {
      return false;
    }

    return document.documentElement.getAttribute('data-theme') === 'dark';
  }

  private applyTheme(isDarkTheme: boolean): void {
    if (typeof document === 'undefined') {
      return;
    }

    document.documentElement.setAttribute('data-theme', isDarkTheme ? 'dark' : 'light');
  }

  private readStoredThemePreference(): 'light' | 'dark' | null {
    try {
      const value = localStorage.getItem(THEME_STORAGE_KEY);
      return value === 'light' || value === 'dark' ? value : null;
    } catch {
      return null;
    }
  }

  private storeThemePreference(isDarkTheme: boolean): void {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, isDarkTheme ? 'dark' : 'light');
    } catch {
      // Local storage may be unavailable in restricted browser contexts.
    }
  }

}
